import { getClient } from '@api/client';
import {
  UserActivityStatus,
  UserAvailabilityActivityResponse,
  UserSetAvailabilityStatus,
  UserStats,
} from '@api/generated/graphql';
import { retryMutation } from '@api/lib/retryMutate';
import { CombinedGraphQLErrors } from '@apollo/client';
import { GraphQLExtensionDetails } from '@api/lib/graphqlTypes';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import { isIsoBefore } from '@lib/utils/time';
import AuthModule from '@modules/AuthModule';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';
import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import {
  CANCEL_WRAPUP_FAILED,
  UPDATE_STATUS_FAILED,
} from '../../constants/errorCodes';
import {
  CANCEL_WRAPUP_MUTATION,
  UPDATE_USER_AVAILABILITY_STATUS_MUTATION,
} from '../../constants/operations';
import cancelWrapupMutation from './data/cancelWrapupMutation';
import updateUserAvailabilityStatusMutation from './data/updateUserAvailabilityStatusMutation';
import { ReadonlyServiceQueue } from './types';
import {
  onAgentRecoveryStarted,
  OnAgentRecoveryStartedProps,
} from './workspace/events/agent/handleAgentRecoveryStarted';
import getQueue from './workspace/lib/getQueue';
import { getUser } from './workspace/lib/requireUser';
import { ServiceUserUtils } from './workspace/lib/serviceUserUtils';
import {
  getSession,
  getSessionByConversationSid,
} from './workspace/lib/sessions';
import { serviceState } from './workspace/lib/state';
import {
  getLiveServicesMappingByPhoneAndTenant,
  getLiveServicesTenantId,
} from './workspace/lib/liveServicesMapping';
import ServiceEvents, {
  ServiceSessionEventType,
} from './workspace/types/ServiceEvents';
import ServiceSession from './workspace/types/ServiceSession';
import ServiceUser from './workspace/types/ServiceUser';
import { closeWorkspace, openWorkspace } from './workspace/workspace';
import { clearTimeDiff } from './workspace/lib/clientServerTimeDiff';
import { recordAgentStatusChange } from './metrics/agentStatusChangeHelpers';
import { stopConnectionStateMonitoring } from './workspace/lib/connectionStateMonitor';
import { Source } from './metrics/agentMetrics';
import { queueSessionSyncManager } from './workspace/lib/QueueSessionSyncManager';

export type { ServiceUser };
export { ServiceSessionEventType } from './workspace/types/ServiceEvents';
export type {
  ServiceSessionEvent,
  ServiceSessionUserEvent,
  ServiceSessionOfferedEvent,
  ServiceSessionCreatedEvent,
  ServiceSessionRecoveryEvent,
} from './workspace/types/ServiceEvents';

const USER_STATUS_UPDATE_EVENT_SOURCE = 'user-update-status';

export class ServiceModule extends AppModule {
  override events = [
    'user.status.updated',
    'user.status.force_away',
    ...ServiceEvents,
  ];
  override preload(): void {
    const initializeWorkspace = async (user: AuthUser) => {
      if (!user.org.telecomConfig) {
        this.app.logger.error(
          'No telecom config found for user organization. This may indicate an authentication issue or missing configuration.',
        );
        return;
      }
      openWorkspace(
        user.org.telecomConfig,
        user.id,
        user.accountId,
        user.org.tenants.map((t) => t.id),
        user.isLiveServicesNetwork,
        this.app.logger,
      );
    };

    // Wait for both auth.signin and featureflags.initialized; re-arm for future pairs
    this.app.events.onAll(
      [AuthEvents.SIGNIN, 'featureflags.initialized'],
      (user: AuthUser) => {
        initializeWorkspace(user);
      },
    );

    this.app.events.on(AuthEvents.SIGNOUT, () => {
      // Clear time sync data so it's recalculated on next login
      clearTimeDiff();
      closeWorkspace();
    });
  }

  override load(): void {
    //
  }

  public unload(): void {
    // Stop connection state monitoring to prevent memory leaks
    stopConnectionStateMonitoring();
  }

  public async refetchWorkspace() {
    const user = AuthModule.user;
    if (user) {
      if (!user.org.telecomConfig) {
        this.app.logger.error(
          'No telecom config found for user organization during workspace refetch. Config may have been lost during user refresh.',
        );
        return;
      }
      openWorkspace(
        user.org.telecomConfig,
        user.id,
        user.accountId,
        user.org.tenants.map((t) => t.id),
        user.isLiveServicesNetwork,
        this.app.logger,
      );
    }
  }

  /**
   * Updates the user status by calling the server mutation and returned value is set to the local state.
   * Performs a complete update operation including server sync and Valtio state management.
   */
  public async updateUserStatusV2(
    userId: string,
    status: UserSetAvailabilityStatus,
  ) {
    try {
      this.app.logger.debug('Setting user status V2', { userId, status });

      // Set loading state in ServiceModule
      this.setUserStatusLoading(userId, true);

      // Get user to check for single cancel wrap-up case
      const user = serviceState.userById[userId];
      if (!user) {
        this.app.logger.error('User not found', { userId });
        throw new Error('User not found');
      }

      // Capture current status for metrics (before change)
      const previousStatus = ServiceUserUtils.getAvailabilityStatus(user);

      // Use Case 1: Single Cancel Wrap-Up
      const isSingleCancelWrapup = this.isSingleCancelWrapup(userId, status);

      if (isSingleCancelWrapup) {
        this.app.logger.debug('Single cancel wrap-up detected', { userId });
        const cancelRes = await this.cancelWrapup(userId);

        this.app.events.emit(ServiceSessionEventType.RECOVERY_COMPLETED, {
          userId,
          source: USER_STATUS_UPDATE_EVENT_SOURCE,
        });
        this.setUserActivityAndStatsIfNewer(
          userId,
          UserActivityStatus.None,
          cancelRes?.stats,
        );
      } else {
        // Use Case 2: General Status Change Flow
        this.app.logger.debug('General status change flow', { userId, status });

        // Call setUserAvailabilityStatus mutation
        const updateRes = await this.updateUserAvailabilityStatus(
          userId,
          status,
        );
        const activityStatus =
          updateRes?.availabilityActivityStatus?.activityStatus;
        // Check if we need to call cancelWrapup after successful status update
        // Changing from Available, Wrapup to Break receives event wrapup ended
        if (
          activityStatus === UserActivityStatus.WrapUp &&
          status === UserSetAvailabilityStatus.Available
        ) {
          // This case shouldn't happen due to event that we receive from RTCS when
          // changing from (Available, Wrapup) to Any status that is not Available
          // In this case Wrapup should be canceled automatically
          this.app.logger.debug('Calling cancelWrapup after status update', {
            userId,
          });
          const cancelRes = await this.cancelWrapup(userId);
          this.app.events.emit(ServiceSessionEventType.RECOVERY_COMPLETED, {
            userId,
            source: USER_STATUS_UPDATE_EVENT_SOURCE,
          });
          this.setUserActivityAndStatsIfNewer(
            userId,
            UserActivityStatus.None,
            cancelRes?.stats,
          );
        } else {
          this.setUserActivityAndStatsIfNewer(
            userId,
            activityStatus,
            updateRes?.stats,
          );
        }
      }

      this.setUserStatus(userId, status);

      // Track status change for baseline metrics (manual source)
      recordAgentStatusChange(previousStatus, status, Source.MANUAL);

      // V2 implementation - enhanced logic for phase2 features
      if (status === UserSetAvailabilityStatus.Available) {
        this.app.events.emit('userStatus.updated.available');
      }
    } catch (error) {
      let err = error;
      let message;

      if (error instanceof Error) {
        // Preserve the original Error object (including specific error names)
        message = error.message;
        err = error;
      } else {
        message = extractErrorMessage(error);
        err = new Error(message);
      }

      this.setUserStatusError(userId, message);
      throw err;
    } finally {
      this.setUserStatusLoading(userId, false);
    }
  }

  /**
   * Cancels the wrapup state for the specified user.
   * @param userId - The ID of the user whose wrapup should be cancelled.
   */
  private async cancelWrapup(
    userId: string,
  ): Promise<UserAvailabilityActivityResponse | null> {
    try {
      this.app.logger.debug('Cancelling wrapup', { userId });

      // Call cancelWrapup mutation (Apollo handles retries automatically)
      const { data, error } = await retryMutation(
        () =>
          getClient().mutate({
            mutation: cancelWrapupMutation,
            variables: { userId },
          }),
        {
          operationName: CANCEL_WRAPUP_MUTATION,
          shouldRetryForGraphQLErrors: (errOrResult) => {
            if (CombinedGraphQLErrors.is(errOrResult)) {
              const status = (errOrResult.errors[0] as GraphQLExtensionDetails)
                ?.extensions?.problemDetails?.status;
              return (
                !!status && (status === 408 || (status >= 500 && status <= 599))
              );
            }
            return false;
          },
        },
      );

      if (!data || CombinedGraphQLErrors.is(error)) {
        const cancelWrapupError = new Error('Cancel wrapup failed');
        cancelWrapupError.name = CANCEL_WRAPUP_FAILED;
        throw cancelWrapupError;
      }

      this.app.logger.debug('cancelWrapup succeeded', { userId });
      return data.cancelUserWrapupActivity ?? null;
    } catch (error) {
      this.app.logger.error('Failed to cancel wrapup after status update', {
        userId,
        error: error instanceof Error ? error.message : String(error),
      });

      if (error instanceof Error) {
        error.name = CANCEL_WRAPUP_FAILED;
        throw error;
      } else {
        const err = new Error(String(error));
        err.name = CANCEL_WRAPUP_FAILED;
        throw err;
      }
    }
  }

  private async updateUserAvailabilityStatus(
    userId: string,
    status: UserSetAvailabilityStatus,
  ): Promise<UserAvailabilityActivityResponse> {
    this.app.logger.debug('Updating user availability status', {
      userId,
      status,
    });

    try {
      const { data, error } = await retryMutation(
        () =>
          getClient().mutate({
            mutation: updateUserAvailabilityStatusMutation,
            variables: { userId, status },
          }),
        {
          operationName: UPDATE_USER_AVAILABILITY_STATUS_MUTATION,
          shouldRetryForGraphQLErrors: (errOrResult) => {
            if (CombinedGraphQLErrors.is(errOrResult)) {
              const status = (errOrResult.errors[0] as GraphQLExtensionDetails)
                ?.extensions?.problemDetails?.status;
              return (
                !!status && (status === 408 || (status >= 500 && status <= 599))
              );
            }
            return false;
          },
        },
      );

      const payload = data?.setUserAvailabilityStatus ?? null;

      if (!payload || CombinedGraphQLErrors.is(error)) {
        const statusError = new Error('No user availability status response');
        statusError.name = UPDATE_STATUS_FAILED;
        throw statusError;
      }

      this.app.logger.debug('updateUserAvailabilityStatus succeeded', {
        userId,
        status,
      });
      return payload;
    } catch (error) {
      this.app.logger.error('Failed to update user availability status', {
        userId,
        status,
        error: error instanceof Error ? error.message : String(error),
      });
      if (error instanceof Error) {
        error.name = UPDATE_STATUS_FAILED;
        throw error;
      } else {
        const err = new Error(String(error));
        err.name = UPDATE_STATUS_FAILED;
        throw err;
      }
    }
  }

  /**
   * Updates the user status by calling the server mutation and returned value is set to the local state.
   * Performs a complete update operation including server sync and Valtio state management.
   */
  public async updateUserStatus(
    userId: string,
    status: UserSetAvailabilityStatus,
  ) {
    return this.updateUserStatusV2(userId, status);
  }

  /**
   * Gets the current loading state for a user status update.
   */
  public getUserStatusUpdateLoading(userId: string): boolean {
    return serviceState.updateUserStatusById[userId]?.loading ?? false;
  }

  /**
   * Sets the loading state for a user status update.
   */
  public setUserStatusLoading(userId: string, loading: boolean): void {
    serviceState.updateUserStatusById[userId] ??= {
      loading: false,
      error: undefined,
    };
    serviceState.updateUserStatusById[userId].loading = loading;

    // Clear error when setting loading to true
    if (loading) {
      serviceState.updateUserStatusById[userId].error = undefined;
    }
  }

  /**
   * Sets the error state for a user status update.
   */
  public setUserStatusError(userId: string, error?: string): void {
    serviceState.updateUserStatusById[userId] ??= {
      loading: false,
      error: undefined,
    };
    serviceState.updateUserStatusById[userId].error = error;
  }

  /**
   * Updates the user's status in the ServiceModule state.
   */
  public setUserStatus(
    userId: string,
    status: UserSetAvailabilityStatus,
  ): void {
    const user = serviceState.userById[userId];
    if (user) {
      ServiceUserUtils.setAvailabilityStatus(user, status);
    }
  }

  /**
   * Updates the user's activity status in the ServiceModule state.
   */
  public setUserActivityAndStatsIfNewer(
    userId: string,
    activityStatus: UserActivityStatus,
    stats?: UserStats | null,
  ): void {
    const user = serviceState.userById[userId];
    if (!stats && ServiceUserUtils.getActivityStatus(user) !== activityStatus) {
      ServiceUserUtils.setActivityStatus(user, activityStatus);
      return;
    }
    const updatedAt = stats?.lastActionAt;
    const existingAt = user.stats?.lastActionAt;
    if (!updatedAt) {
      return;
    }
    if (!existingAt || isIsoBefore(existingAt, updatedAt)) {
      // Update only when the incoming timestamp is newer than the existing one
      ServiceUserUtils.setActivityStatus(user, activityStatus);
      if (stats) {
        user.stats = stats;
      }
    }
  }

  /**
   * Updates the current user's status in the ServiceModule state.
   */
  public setCurrentUserStatus(status: UserSetAvailabilityStatus): void {
    const currentUserId = serviceState.id;
    if (currentUserId) {
      this.setUserStatus(currentUserId, status);
    }
  }

  /**
   * Updates the user's recovery time.
   * Used for wrapup time management.
   */
  public setUserRecoveryState(userId: string, recoveryTime?: number): void {
    const user = serviceState.userById[userId];
    if (user) {
      if (recoveryTime !== undefined) {
        user.recoveryTime = recoveryTime;
      }
    }
  }

  /**
   * Determines whether the status update qualifies for the single cancel wrap-up flow.
   * Conditions:
   * - Current user activity is WrapUp
   * - Current user availability is Available
   * - Target status is Available
   */
  private isSingleCancelWrapup(
    userId: string,
    targetStatus: UserSetAvailabilityStatus,
  ): boolean {
    const user = serviceState.userById[userId];
    if (!user) return false;
    const currentActivity = ServiceUserUtils.getActivityStatus(user);
    const currentAvailability = ServiceUserUtils.getAvailabilityStatus(user);
    const isWrapUp = currentActivity === UserActivityStatus.WrapUp;
    const isCurrentlyAvailable =
      currentAvailability === UserSetAvailabilityStatus.Available;
    const isTargetAvailable =
      targetStatus === UserSetAvailabilityStatus.Available;
    return isWrapUp && isCurrentlyAvailable && isTargetAvailable;
  }

  /**
   * Gets the current user from ServiceModule state.
   */
  public getCurrentUser() {
    const currentUserId = serviceState.id;
    return currentUserId ? serviceState.userById[currentUserId] : null;
  }

  /**
   * Gets the current userId from ServiceModule state.
   */
  public getCurrentUserId() {
    return serviceState.id;
  }

  public isLiveServicesNetwork(): boolean {
    return serviceState.isLiveServicesNetwork;
  }

  public hasLiveServicesMapping(
    phoneNumber?: string,
    tenantId?: string,
  ): boolean {
    if (!phoneNumber || !tenantId) {
      return false;
    }

    return Boolean(
      getLiveServicesMappingByPhoneAndTenant(
        phoneNumber,
        tenantId,
        serviceState.liveServicesMappings,
      ),
    );
  }

  /**
   * Resolves the Live Services customer tenant for a dialed number, falling back to the
   * passed-in tenant id when no mapping matches.
   */
  public resolveLiveServicesTenantId(
    rtcsTenantId: number,
    phoneNumber: string,
  ): string {
    return getLiveServicesTenantId(
      rtcsTenantId,
      phoneNumber,
      serviceState.liveServicesMappings,
    );
  }

  /**
   * Sets the current userId to ServiceModule state.
   */
  public setCurrentUserId(userId: string | null) {
    serviceState.id = userId;
  }

  /**
   * Returns true if there is a session with the given conversationSid and that if
   * this session has an agentId, it is equal to the given userId.
   *
   * Note: we do the extra user check to handle the transfer case. The agentId is added
   * when the session is delivered. If the call is transferred to another agent, then
   * it is possible that the session is still there and has different agentId attached. When we do
   * this extra check, then this would return false for transfers and
   * help us handle a call transfer as an internal calls as we were doing before (until the internal/internal
   * work is done).
   *
   * @param conversationSid
   * @param userId
   */
  public hasUserSession(conversationSid: string, userId: string): boolean {
    const session = Object.values(serviceState.sessions).find(
      (s) => s.conversationSid === conversationSid,
    );

    if (!session) {
      return false;
    }

    // it's not this user's session if it has a different agentId attached
    const agentId = getUser(userId)?.contactCenterUserId ?? '';
    if (session.agentId && session.agentId !== agentId) {
      return false;
    }

    // there is a session and it has this user's agentId attached
    return true;
  }

  public getSession(conversationId: string): ServiceSession | null {
    return getSession(conversationId);
  }

  public getSessionByConversationSid(
    conversationSid: string,
  ): ServiceSession | null {
    return getSessionByConversationSid(conversationSid);
  }

  public getQueue(id: string): ReadonlyServiceQueue | null {
    return getQueue(id);
  }

  //Only used when the user is in wrapup state and on refresh
  public putAgentInWrapupState(props: OnAgentRecoveryStartedProps) {
    return onAgentRecoveryStarted(props);
  }

  public isUserAvailable(user: ServiceUser): boolean {
    return ServiceUserUtils.isUserAvailable(user);
  }

  /**
   * Stricter "available" check that mirrors the call center monitoring
   * "Capacity" widget: the user must have Available availability AND an
   * activity that allows them to be offered a queue call (`None` or
   * `CallOffer`). Use this to count agents who could actually take a call
   * right now, not merely those who have set themselves to Available.
   */
  public isUserAvailableForCalls(user: ServiceUser): boolean {
    return ServiceUserUtils.computeUserAvailability(user).isAvailable;
  }

  public getAvailabilityStatus(user: ServiceUser): UserSetAvailabilityStatus {
    return ServiceUserUtils.getAvailabilityStatus(user);
  }

  public mountQueueSessionSync(): void {
    queueSessionSyncManager.mount();
  }

  public unmountQueueSessionSync(): void {
    queueSessionSyncManager.unmount();
  }
}

export default new ServiceModule(
  appManager.events,
  appManager.logger('Service'),
);
